import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import parse from 'html-react-parser';
import { useHubspotForm } from '@aaronhayes/react-use-hubspot-form';
import { useInputBool, useInputText } from '@/hooks';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { userSelectors } from '@/redux/selectors';
import { userActions } from '@/redux/actions';
import {
  GoogleAuthOneTapRequest,
  GoogleTokenTwoFARequest,
  SignupRequestExtended,
  TestEmailRequest,
  amlbotAPI,
  AmlPreviewData,
} from '@/api';
import { Validation } from '@/models';
import {
  COMMON_CONTAINER_ID,
  EMAIL_REGEX,
  PASSWORD_REGEX,
  externalLinks,
} from '@/config';
import { executeCaptcha, reportSignup } from '@/utils';
import { toast } from 'react-toastify';
import { useHotkeys } from 'react-hotkeys-hook';

import {
  AlertBox,
  Button,
  Input,
  InputCheckbox,
  SocialAuthButtons,
  Support,
} from '@/components';

import styles from './SignUpForm.module.sass';

const PORTAL_ID = (import.meta.env.VITE_REACT_APP_HUBSPOT_PORTAL_ID as string)!;
const SIGNUP_FORM_ID = (import.meta.env
  .VITE_REACT_APP_HUBSPOT_SIGNUP_FORM_ID as string)!;
const GOOGLE_CLIENT_ID = (import.meta.env
  .VITE_REACT_APP_GOOGLE_CLIENT_ID as string)!;

interface SignUpFormProps {
  amlPreviewData?: AmlPreviewData;
  hidePasswordConfirmation?: boolean;
}

export const SignUpForm = (props: SignUpFormProps) => {
  const { amlPreviewData, hidePasswordConfirmation = false } = props;
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const { error, loading } = useAppSelector(userSelectors.getUserSlice);
  const formDivId = 'hubspot-form-wrapper';
  const [hubspotForm, setHubspotForm] = useState<HTMLFormElement | null>();
  const focusedInputRef = useRef<HTMLInputElement | null>(null);
  const theInterval = useRef<any>(null);
  const [submitCounter, setSubmitCounter] = useState(0);
  // Captcha and the email check run before the signup request starts, so the
  // store's `loading` alone leaves the button idle for that time.
  const [isPreparing, setIsPreparing] = useState(false);

  useHubspotForm({
    portalId: PORTAL_ID,
    formId: SIGNUP_FORM_ID,
    target: `#${formDivId}`,
    onFormSubmitted: () => {
      console.log('submitted');
    },
    onFormReady: (form) => setHubspotForm(() => form),
  });

  useEffect(() => {
    dispatch(userActions.setError(null));

    return () => {
      clearInterval(theInterval.current);
      theInterval.current = null;
    };
  }, [dispatch]);

  useEffect(() => {
    if (submitCounter === 5) {
      theInterval.current = setInterval(() => {
        setSubmitCounter((prevCounter) => prevCounter - 1);
      }, 1000);
    }
    if (submitCounter === 0) {
      clearInterval(theInterval.current);
      theInterval.current = null;
    }
  }, [submitCounter]);

  const hsCallback = useCallback(
    (email: string) => {
      try {
        if (hubspotForm) {
          const emailInput = hubspotForm.querySelector(
            'input[name="email"]',
          ) as HTMLInputElement;

          emailInput.value = email;

          hubspotForm.submit();
        }
      } catch (err) {
        console.log('HUBSPOT ERROR', err);
      }
    },
    [hubspotForm],
  );

  const gaCallback = () => {
    reportSignup();
  };

  useEffect(() => {
    if (window.google?.accounts) {
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        context: 'signup',
        cancel_on_tap_outside: false,
        callback: (response: any) => {
          const { credential } = response;
          const payload: GoogleAuthOneTapRequest = {
            token: credential,
          };
          payload.hsCallback = hsCallback;
          payload.gaCallback = gaCallback;

          if (!!amlPreviewData?.uid) {
            payload.amlCheckFor = amlPreviewData.uid;
          }

          dispatch(userActions.googleAuthOneTap(payload));
        },
        use_fedcm_for_prompt: true,
      });
      window.google.accounts.id.prompt();
    }

    return () => {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.cancel();
      }
    };
  }, [window.google, amlPreviewData]);

  const login = useGoogleLogin({
    flow: 'auth-code',
    onSuccess: async (codeResponse) => {
      const payload: GoogleTokenTwoFARequest = { code: codeResponse.code };
      payload.hsCallback = hsCallback;
      payload.gaCallback = gaCallback;

      if (!!amlPreviewData?.uid) {
        payload.amlCheckFor = amlPreviewData.uid;
      }

      dispatch(userActions.googleAuth(payload));
    },
    onError: (errorResponse) => {
      userActions.setError(errorResponse.error || 'Something went wrong');
    },
  });

  const EMAIL_VALIDATIONS: Validation[] = useMemo(
    () => [
      {
        name: 'required',
        value: true,
        message: t('translation.field_is_required'),
      },
      {
        name: 'email',
        value: EMAIL_REGEX,
        message: t('translation.invalid_email'),
      },
      {
        name: 'maxLength',
        value: 200,
        message: t('translation.max_length_error', { length: 200 }),
      },
    ],
    [t],
  );

  const PASSWORD_VALIDATIONS: Validation[] = useMemo(
    () => [
      {
        name: 'required',
        value: true,
        message: t('translation.field_is_required'),
      },
      {
        name: 'regex',
        value: PASSWORD_REGEX,
        message: t('translation.reset_pwd_error_hint_length'),
      },
    ],
    [t],
  );

  const POLICY_VALIDATIONS: Validation[] = useMemo(
    () => [
      {
        name: 'required',
        value: true,
        message: t('translation.field_is_required'),
      },
    ],
    [t],
  );

  const SUBSCRIPTION_VALIDATIONS: Validation[] = useMemo(() => [], []);

  const emailField = useInputText('', EMAIL_VALIDATIONS);
  const passwordField = useInputText('', PASSWORD_VALIDATIONS);
  const policyField = useInputBool(false, POLICY_VALIDATIONS);
  const subscriptionField = useInputBool(false, SUBSCRIPTION_VALIDATIONS);

  const CONFIRM_PASSWORD_VALIDATIONS: Validation[] = useMemo(() => {
    return !hidePasswordConfirmation
      ? [
          {
            name: 'required',
            value: true,
            message: t('translation.field_is_required'),
          },
          {
            name: 'equal',
            value: passwordField.value,
            message: t('translation.password_mismatch'),
          },
        ]
      : [];
  }, [passwordField.value, t, hidePasswordConfirmation]);

  const confirmPasswordField = useInputText('', CONFIRM_PASSWORD_VALIDATIONS);

  const testProvidedEmail = useCallback(async () => {
    const payload: TestEmailRequest = { capture: emailField.value };
    try {
      const response = await amlbotAPI.testEmail(payload);

      if (!response.success) {
        toast.error(t('translation.invalid_email'), {
          containerId: COMMON_CONTAINER_ID,
        });

        return false;
      }
    } catch {
      //
    }
    return true;
  }, [emailField.value, t]);

  const submitHandler = useCallback(async () => {
    if (!loading && !isPreparing) {
      if (
        emailField.isValid &&
        passwordField.isValid &&
        confirmPasswordField.isValid &&
        policyField.isValid
      ) {
        setIsPreparing(true);
        try {
          const token = await executeCaptcha();
          if (!token) {
            return;
          }

          const emailValid = await testProvidedEmail();

          if (!emailValid) {
            setSubmitCounter(() => 5);
            return;
          }

          const payload: SignupRequestExtended = {
            emailAddress: emailField.value as string,
            password: passwordField.value as string,
            needNews: subscriptionField.value as boolean,
            locale: i18n.language,
            termsAccepted: policyField.value as boolean,
            token,
            hsCallback,
            gaCallback,
          };

          if (!!amlPreviewData?.uid) {
            payload.amlCheckFor = amlPreviewData.uid;
          }

          dispatch(userActions.signup(payload));
        } finally {
          setIsPreparing(false);
        }
      } else {
        if (!emailField.isValid) {
          emailField.onBlur();
        }
        if (!passwordField.isValid) {
          passwordField.onBlur();
        }
        if (!hidePasswordConfirmation && !confirmPasswordField.isValid) {
          confirmPasswordField.onBlur();
        }
        if (!policyField.isValid) {
          policyField.onBlur();
        }
      }
    }
  }, [
    loading,
    isPreparing,
    emailField,
    passwordField,
    confirmPasswordField,
    policyField,
    testProvidedEmail,
    subscriptionField.value,
    i18n.language,
    hsCallback,
    dispatch,
    amlPreviewData?.uid,
    hidePasswordConfirmation,
  ]);

  useHotkeys(
    'enter',
    (event) => {
      event.preventDefault();
      focusedInputRef.current = document.querySelector(
        'input:focus',
      ) as HTMLInputElement;
      if (focusedInputRef.current) {
        focusedInputRef.current.blur();
        submitHandler();
      }
    },
    { enableOnFormTags: true },
    [],
  );

  useEffect(() => {
    if (!loading && focusedInputRef.current) {
      focusedInputRef.current.focus();
    }
  }, [loading]);

  return (
    <>
      {!!error && (
        <div className={styles.alert}>
          <AlertBox type="danger">
            {{
              title: parse(t('translation.error')),
              description: error,
              icon: '🤔',
            }}
          </AlertBox>
          <AlertBox type="info">
            {{
              title: t('translation.tips'),
              description: (
                <div className={styles.tips}>
                  <ul className={styles.list}>
                    <li>{t('translation.tips_signup_try_to_login')}</li>
                    <li>
                      <span className={styles.gap}>
                        {t('translation.tips_signup_try_to_restore_part1')}
                      </span>
                      <Link className={styles.link} to="/password/forgot">
                        {t('translation.tips_signup_try_to_restore_part2')}
                      </Link>
                    </li>
                    <li>
                      <div className={styles.wrapper}>
                        <span className={styles.gap}>
                          {t('translation.contact_support')}
                        </span>
                        <Support />
                      </div>
                    </li>
                  </ul>
                </div>
              ),
            }}
          </AlertBox>
        </div>
      )}

      <div id="hubspot-form-wrapper" style={{ height: 0, width: 0 }} />

      <div className={styles.fields}>
        <div>
          <Input
            id="email"
            name="email"
            type="text"
            value={emailField.value as string}
            onChange={emailField.onChange}
            onBlur={emailField.onBlur}
            placeholder={t('translation.email') || ''}
            disabled={loading}
            isError={emailField.isDirty && !emailField.isValid}
            required
            floating
          />
          {emailField.isDirty && !emailField.isValid && (
            <AlertBox type="danger" small>
              {{
                description: emailField.errors[0],
              }}
            </AlertBox>
          )}
        </div>

        <div>
          <Input
            id="password"
            name="password"
            type="password"
            value={passwordField.value as string}
            onChange={passwordField.onChange}
            onBlur={passwordField.onBlur}
            placeholder={t('translation.password') || ''}
            disabled={loading}
            isError={passwordField.isDirty && !passwordField.isValid}
            required
            floating
          />
          {passwordField.isDirty && !passwordField.isValid && (
            <AlertBox type="danger" small>
              {{
                description: passwordField.errors[0],
              }}
            </AlertBox>
          )}
        </div>

        {!hidePasswordConfirmation && (
          <div>
            <Input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              value={confirmPasswordField.value as string}
              onChange={confirmPasswordField.onChange}
              onBlur={confirmPasswordField.onBlur}
              placeholder={t('translation.confirm_password') || ''}
              disabled={loading}
              isError={
                confirmPasswordField.isDirty && !confirmPasswordField.isValid
              }
              required
              floating
            />
            {confirmPasswordField.isDirty && !confirmPasswordField.isValid && (
              <AlertBox type="danger" small>
                {{
                  description: confirmPasswordField.errors[0],
                }}
              </AlertBox>
            )}
          </div>
        )}

        <div>
          <InputCheckbox
            id="policy"
            name="policy"
            value={policyField.value as boolean}
            onChange={policyField.onChange}
            isError={policyField.isDirty && !policyField.isValid}
            required
          >
            <span>
              {parse(
                t('translation.signup_terms_link', {
                  termsLink: '/documents/terms.pdf',
                  policyLink: externalLinks.SERVICE_POLICY_URL,
                }),
              )}
            </span>
          </InputCheckbox>
          {policyField.isDirty && !policyField.isValid && (
            <AlertBox type="danger" small>
              {{
                description: policyField.errors[0],
              }}
            </AlertBox>
          )}
        </div>

        <div>
          <InputCheckbox
            id="notification"
            name="notification"
            value={subscriptionField.value as boolean}
            onChange={subscriptionField.onChange}
          >
            <span>{t('translation.signup_need_news_str')}</span>
          </InputCheckbox>
        </div>
      </div>

      <div className={styles.buttons}>
        <Button
          size="xl"
          corner="rect"
          onClick={submitHandler}
          disabled={!!submitCounter}
          isLoading={loading || isPreparing}
          fullwidth
        >
          {!!submitCounter && submitCounter}
          {!submitCounter && t('translation.create_account')}
        </Button>

        <SocialAuthButtons
          mode="signUp"
          onClickAuthGoogle={login}
          gaCallback={gaCallback}
          disabled={loading}
        />
      </div>
    </>
  );
};
